import React, { lazy, Suspense, useContext } from "react";
import { BrowserRouter, Switch, Redirect, Route as RouterRoute } from "react-router-dom";
import { createPortal } from "react-dom";
import { ToastContainer } from "react-toastify";

import LoggedInLayout from "../layout";
import Login from "../pages/Login";
import PublicCatalog from "../pages/PublicCatalog";
import PublicReport from "../pages/PublicReport";
import { AuthProvider } from "../context/Auth/AuthContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { NotificationsProvider } from "../context/Notifications/NotificationsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";
import BackdropLoading from "../components/BackdropLoading";
import ForgotPassword from "../pages/ForgetPassWord";
import ResetPassword from "../pages/ResetPassword";
import { ForwardMessageProvider } from "../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageProvider } from "../context/ReplyingMessage/ReplyingMessageContext";
import { EditMessageProvider } from "../context/EditingMessage/EditingMessageContext";
import { QueueSelectedProvider } from "../context/QueuesSelected/QueuesSelectedContext";
import PasswordChangeModal from "../components/PasswordChangeModal";
import BillingGate from "../components/Billing/BillingGate";

const lazyWithRetry = (importer) =>
  lazy(() =>
    importer().catch(async (error) => {
      const isChunkError =
        error?.name === "ChunkLoadError" ||
        /Loading chunk .* failed/i.test(error?.message || "");

      if (isChunkError) {
        // Tenta buscar o chunk novamente após 500ms (resolve oscilações momentâneas de rede)
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          return await importer();
        } catch (retryError) {
          // Se falhou novamente, só recarrega se ainda não tiver recarregado nesta rota
          const key = "chunk_retry_" + (window.location.pathname || "");
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            window.location.reload();
            return new Promise(() => {});
          }
          throw retryError;
        }
      }
      throw error;
    })
  );

// Signup e lazy: so e alcancavel por /signup, mas sendo import estatico
// arrastava formik + yup + query-string (~55 KB) para o main.js, que toda
// sessao ja autenticada baixa sem nunca abrir a tela de cadastro.
const Signup = lazyWithRetry(() => import("../pages/Signup"));
const Dashboard = lazyWithRetry(() => import("../pages/Dashboard"));
const TicketResponsiveContainer = lazyWithRetry(() => import("../pages/TicketResponsiveContainer"));
const Connections = lazyWithRetry(() => import("../pages/Connections"));
const SettingsCustom = lazyWithRetry(() => import("../pages/SettingsCustom"));
const Financeiro = lazyWithRetry(() => import("../pages/Financeiro"));
const BillingPaymentPage = lazyWithRetry(() => import("../pages/BillingPayment"));
const ProductsPage = lazyWithRetry(() => import("../pages/Commercial/ProductsPage"));
const ServicesPage = lazyWithRetry(() => import("../pages/Commercial/ServicesPage"));
const ConsortiumDashboard = lazyWithRetry(() => import("../modules/consortium/pages/Dashboard"));
const SchedulingPage = lazyWithRetry(() => import("../pages/Scheduling"));
const ConsortiumCardsPage = lazyWithRetry(() => import("../modules/consortium/pages/Cards"));
const ConsortiumOpportunitiesPage = lazyWithRetry(() => import("../modules/consortium/pages/Opportunities"));
const ConsortiumAdministratorsPage = lazyWithRetry(() => import("../modules/consortium/pages/Administrators"));
const ConsortiumTypesPage = lazyWithRetry(() => import("../modules/consortium/pages/Types"));
const ProposalsPage = lazyWithRetry(() => import("../pages/Commercial/ProposalsPage"));
const Users = lazyWithRetry(() => import("../pages/Users"));
const Contacts = lazyWithRetry(() => import("../pages/Contacts"));
const Wallets = lazyWithRetry(() => import("../pages/Wallets"));
const ContactImportPage = lazyWithRetry(() => import("../pages/Contacts/import"));
const ChatMoments = lazyWithRetry(() => import("../pages/Moments"));
const Queues = lazyWithRetry(() => import("../pages/Queues"));
const Tags = lazyWithRetry(() => import("../pages/Tags"));
const MessagesAPI = lazyWithRetry(() => import("../pages/MessagesAPI"));
const Helps = lazyWithRetry(() => import("../pages/Helps"));
const ContactLists = lazyWithRetry(() => import("../pages/ContactLists"));
const ContactListItems = lazyWithRetry(() => import("../pages/ContactListItems"));
const InvoicesManager = lazyWithRetry(() => import("../pages/Invoices"));
const QuickMessages = lazyWithRetry(() => import("../pages/QuickMessages"));
const Catalog = lazyWithRetry(() => import("../pages/Catalog"));
const Schedules = lazyWithRetry(() => import("../pages/Schedules"));
const Campaigns = lazyWithRetry(() => import("../pages/Campaigns"));
const CampaignReport = lazyWithRetry(() => import("../pages/CampaignReport"));
const CampaignsConfig = lazyWithRetry(() => import("../pages/CampaignsConfig"));
const Annoucements = lazyWithRetry(() => import("../pages/Annoucements"));
const Chat = lazyWithRetry(() => import("../pages/Chat"));
const Prompts = lazyWithRetry(() => import("../pages/Prompts"));
const AIProfile = lazyWithRetry(() => import("../pages/AIProfile"));
const TrackZapConfig = lazyWithRetry(() => import("../pages/TrackZapConfig"));
const AllConnections = lazyWithRetry(() => import("../pages/AllConnections"));
const Reports = lazyWithRetry(() => import("../pages/Reports"));
const FlowBuilderConfig = lazyWithRetry(() =>
  import("../pages/FlowBuilderConfig").then((module) => ({ default: module.FlowBuilderConfig }))
);
const FlowBuilder = lazyWithRetry(() => import("../pages/FlowBuilder"));
const CampaignsPhrase = lazyWithRetry(() => import("../pages/CampaignsPhrase"));
const QueueIntegration = lazyWithRetry(() => import("../pages/QueueIntegration"));
const Files = lazyWithRetry(() => import("../pages/Files"));
const ToDoList = lazyWithRetry(() => import("../pages/ToDoList"));
const Kanban = lazyWithRetry(() => import("../pages/Kanban"));
const GlobalConfig = lazyWithRetry(() => import("../pages/GlobalConfig"));
const MechanicDashboard = lazyWithRetry(() => import("../pages/MechanicDashboard"));
const Mechanics = lazyWithRetry(() => import("../pages/Mechanics"));
const CompaniesMaster = lazyWithRetry(() => import("../pages/CompaniesMaster"));
const Departments = lazyWithRetry(() => import("../pages/Departments"));
const LeadSources = lazyWithRetry(() => import("../pages/LeadSources"));
const GoogleCalendarIntegration = lazyWithRetry(() => import("../pages/GoogleCalendarIntegration"));

const Routes = () => {
  const AppSwitch = () => {
    const { isAuth, loading } = useContext(AuthContext);

    if (loading) {
      return <BackdropLoading />;
    }

    return (
      <Suspense fallback={<BackdropLoading />}>
      <Switch>
        {!isAuth && (
          <RouterRoute exact path="/">
            <Redirect to="/login" />
          </RouterRoute>
        )}
        <Route exact path="/login" component={Login} />
        <Route exact path="/signup" component={Signup} />
        <Route exact path="/forgot-password" component={ForgotPassword} />
        <Route exact path="/reset-password" component={ResetPassword} />
        <RouterRoute exact path="/catalogo/:slug" component={PublicCatalog} />
        <RouterRoute exact path="/relatorio/p/:token" component={PublicReport} />
        <Route exact path="/billing/pay" component={BillingPaymentPage} is_private />
        {isAuth && (
          <WhatsAppsProvider>
            <BillingGate />
            <LoggedInLayout>
              <ForwardMessageProvider>
                <QueueSelectedProvider>
                  <ReplyMessageProvider>
                    <EditMessageProvider>
                      <Switch>
                        <Route exact path="/financeiro" component={Financeiro} is_private />
                        <Route exact path="/products" component={ProductsPage} is_private />
                        <Route exact path="/services" component={ServicesPage} is_private />
                        <Route exact path="/consortiums" component={() => <Redirect to="/consortium/cards" />} is_private />
                        <Route exact path="/consortium" component={ConsortiumDashboard} is_private />
                        <Route exact path="/consortium/dashboard" component={ConsortiumDashboard} is_private />
                        <Route exact path="/consortium/cards" component={ConsortiumCardsPage} is_private />
                        <Route exact path="/consortium/opportunities" component={ConsortiumOpportunitiesPage} is_private />
                        <Route exact path="/scheduling" component={SchedulingPage} is_private />
                        <Route exact path="/consortium/administrators" component={ConsortiumAdministratorsPage} is_private />
                        <Route exact path="/consortium/types" component={ConsortiumTypesPage} is_private />
                        <Route exact path="/proposals" component={ProposalsPage} is_private />
                        <Route exact path="/proposal-settings" component={() => <Redirect to="/budgets" />} is_private />
                        <Route exact path="/budgets" component={ProposalsPage} is_private />
                        <Route exact path="/catalog-settings" component={() => <Redirect to="/budgets" />} is_private />

                        <Route exact path="/invoices-manager" component={InvoicesManager} is_private />
                        <Route exact path="/" component={Dashboard} is_private />
                        <Route exact path="/dashboard" component={Dashboard} is_private />
                        <Route
                          exact
                          path="/tickets/:ticketId?"
                          component={TicketResponsiveContainer}
                          is_private
                        />
                        <Route exact path="/connections" component={Connections} is_private />
                        <Route exact path="/catalog" component={Catalog} is_private />
                        <Route exact path="/quick-messages" component={QuickMessages} is_private />
                        <Route exact path="/todolist" component={ToDoList} is_private />
                        <Route exact path="/schedules" component={Schedules} is_private />
                        <Route exact path="/tags" component={Tags} is_private />
                        <Route exact path="/contacts" component={Contacts} is_private />
                        <Route exact path="/mechanic-dashboard" component={MechanicDashboard} is_private />
                        <Route exact path="/mechanic-checklists" component={MechanicDashboard} is_private />
                        <Route exact path="/mechanic-orders" component={MechanicDashboard} is_private />
                        <Route exact path="/mechanics" component={Mechanics} is_private />
                        <Route exact path="/wallets" component={Wallets} is_private />
                        <Route exact path="/contacts/import" component={ContactImportPage} is_private />
                        <Route exact path="/helps" component={Helps} is_private />
                        <Route exact path="/users" component={Users} is_private />
                        <Route exact path="/messages-api" component={MessagesAPI} is_private />
                        <Route exact path="/settings" component={SettingsCustom} is_private />
                        <Route exact path="/companies-master" component={CompaniesMaster} is_private />
                        <Route exact path="/global-config" component={GlobalConfig} is_private />
                        <Route exact path="/departments" component={Departments} is_private />
                        <Route exact path="/lead-sources" component={LeadSources} is_private />
                        <Route exact path="/integrations/google-calendar" component={GoogleCalendarIntegration} is_private />
                        <Route exact path="/queues" component={Queues} is_private />
                        <Route exact path="/reports" component={Reports} is_private />
                        <Route exact path="/queue-integration" component={QueueIntegration} is_private />
                        <Route exact path="/announcements" component={Annoucements} is_private />
                        <Route exact path="/phrase-lists" component={CampaignsPhrase} is_private />
                        <Route exact path="/flowbuilders" component={FlowBuilder} is_private />
                        <Route exact path="/flowbuilder/:id?" component={FlowBuilderConfig} is_private />
                        <Route exact path="/chats/:id?" component={Chat} is_private />
                        <Route exact path="/files" component={Files} is_private />
                        <Route exact path="/moments" component={ChatMoments} is_private />
                        <Route exact path="/kanban" component={Kanban} is_private />
                        <Route exact path="/prompts" component={Prompts} is_private />
                        <Route exact path="/ai-profile" component={AIProfile} is_private />
                        <Route exact path="/trackzap-config" component={TrackZapConfig} is_private />
                        <Route exact path="/allConnections" component={AllConnections} is_private />
                        <Route exact path="/contact-lists" component={ContactLists} is_private />
                        <Route
                          exact
                          path="/contact-lists/:contact_list_id/contacts"
                          component={ContactListItems}
                          is_private
                        />
                        <Route exact path="/campaigns" component={Campaigns} is_private />
                        <Route exact path="/campaign/:campaignId/report" component={CampaignReport} is_private />
                        <Route exact path="/campaigns-config" component={CampaignsConfig} is_private />
                      </Switch>
                    </EditMessageProvider>
                  </ReplyMessageProvider>
                </QueueSelectedProvider>
              </ForwardMessageProvider>
            </LoggedInLayout>
          </WhatsAppsProvider>
        )}
        {!isAuth && <Redirect to="/login" />}
      </Switch>
      </Suspense>
    );
  };

// Modal global de troca de senha padrao — montado dentro do AuthProvider
const GlobalPasswordChangeModal = () => {
  const { user, showPasswordModal, setShowPasswordModal, isAuth } = useContext(AuthContext);
  const shouldShow = isAuth && (showPasswordModal || !!user?.isDefaultPassword);
  if (!shouldShow) return null;
  return (
    <PasswordChangeModal
      open={true}
      userId={user?.id}
      onSuccess={() => {
        setShowPasswordModal(false);
        if (user) {
          user.isDefaultPassword = false;
        }
        window.location.href = "/tickets";
      }}
    />
  );
};

  return (
    <BrowserRouter>
      <AuthProvider>
        <GlobalPasswordChangeModal />
        <TicketsContextProvider>
          <NotificationsProvider>
            <AppSwitch />
          </NotificationsProvider>
          {createPortal(
            <ToastContainer position="top-center" autoClose={3000} style={{ zIndex: 2000000 }} />,
            document.body
          )}
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
